import React from 'react';
import MapboxMap, {Source, Layer, Popup, NavigationControl, ScaleControl, FullscreenControl} from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Chip from '@mui/material/Chip';
import Alert from '@mui/material/Alert';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';

import {SOURCE_PREFIX_MAP} from '../constants/sources';
import {buildGeoJsonFeatures, GEOJSON_EXPORT_MODES} from '../utils/geoJsonUtils';

const MAPBOX_TOKEN = 'pk.eyJ1IjoiYmxhbmdlNDgiLCJhIjoiY2xkMWhrcjBzMDB1aDNzbGVsdGR5cGU1aSJ9.xcs1ihAFqlazzqNrXNiobQ';

// ── Schémas de couleur ────────────────────────────────────────────────────────

const CLASS_COLORS = {
    A: '#318834', B: '#33A357', C: '#79B752',
    D: '#C3D52A', E: '#EDB731', F: '#E07B27', G: '#CC2027',
};

/** Champs avec palette de classes A-G prédéfinie */
const CLASS_FIELDS = new Set([
    'meta.etiquette_dpe_median', 'meta.etiquette_dpe_moyen',
    'meta.etiquette_ges_median', 'meta.etiquette_ges_moyen',
]);

function buildClassExpr(field) {
    return ['match', ['get', field], ...Object.entries(CLASS_COLORS).flat(), '#aaaaaa'];
}

/** Couleurs de la rampe min→max pour tout autre champ numérique */
const RAMP_MIN_COLOR = '#ffffcc';
const RAMP_MAX_COLOR = '#d73027';

function buildRampExpr(field, minVal, maxVal) {
    if (minVal === maxVal) return RAMP_MAX_COLOR;
    return [
        'case',
        ['!=', ['get', field], null],
        ['interpolate', ['linear'],
            ['to-number', ['get', field]],
            minVal, RAMP_MIN_COLOR,
            maxVal, RAMP_MAX_COLOR,
        ],
        '#dddddd',
    ];
}

// ── Factories de layers (couleur dynamique) ───────────────────────────────────

const clusterLayer = {
    id: 'clusters', type: 'circle', source: 'batiments',
    filter: ['has', 'point_count'],
    paint: {
        'circle-color': ['step', ['get', 'point_count'], '#4fc3f7', 50, '#1976d2', 200, '#0d47a1'],
        'circle-radius': ['step', ['get', 'point_count'], 18, 50, 26, 200, 34],
        'circle-opacity': 0.85,
        'circle-stroke-width': 2, 'circle-stroke-color': '#fff',
    },
};
const clusterCountLayer = {
    id: 'cluster-count', type: 'symbol', source: 'batiments',
    filter: ['has', 'point_count'],
    layout: {'text-field': '{point_count_abbreviated}', 'text-size': 12,
              'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold']},
    paint: {'text-color': '#fff'},
};

function makeUnclusteredLayer(colorExpr) {
    return {
        id: 'unclustered-point', type: 'circle', source: 'batiments',
        // exclure les points stackés (gérés séparément) et les clusters
        filter: ['all', ['!', ['has', 'point_count']], ['!', ['has', '_stackCount']]],
        paint: {
            'circle-color': colorExpr, 'circle-radius': 5,
            'circle-stroke-width': 1.5, 'circle-stroke-color': '#fff', 'circle-opacity': 0.9,
        },
    };
}

/** Layer cercle pour les points co-localisés — couleur dynamique identique aux points individuels */
function makeStackLayer(colorExpr) {
    return {
        id: 'stacked-point', type: 'circle', source: 'batiments',
        filter: ['has', '_stackCount'],
        paint: {
            'circle-color': colorExpr,
            'circle-radius': 10,
            'circle-stroke-width': 2, 'circle-stroke-color': '#fff',
            'circle-opacity': 0.92,
        },
    };
}
/** Badge numérique sur les points stackés */
const stackCountLayer = {
    id: 'stacked-count', type: 'symbol', source: 'batiments',
    filter: ['has', '_stackCount'],
    layout: {
        'text-field': ['to-string', ['get', '_stackCount']],
        'text-size': 11,
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
    },
    paint: {'text-color': '#fff'},
};
/** Badge numérique sur les polygones stackés (centroïde du polygone) */
const stackCountPolygonLayer = {
    id: 'stacked-count-polygon', type: 'symbol', source: 'batiments',
    filter: ['all', ['has', '_stackCount'],
             ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']]],
    layout: {
        'text-field': ['to-string', ['get', '_stackCount']],
        'text-size': 11,
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-anchor': 'center',
    },
    paint: {'text-color': '#fff', 'text-halo-color': '#607d8b', 'text-halo-width': 2},
};
function makePolygonFillLayer(colorExpr) {
    return {
        id: 'polygon-fill', type: 'fill', source: 'batiments',
        filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
        paint: {'fill-color': colorExpr, 'fill-opacity': 0.4},
    };
}
function makePolygonOutlineLayer(colorExpr) {
    return {
        id: 'polygon-outline', type: 'line', source: 'batiments',
        filter: ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']],
        paint: {'line-color': colorExpr, 'line-width': 1.5, 'line-opacity': 0.9},
    };
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Détermine si le mode produit des Points (cluster possible) ou des Polygones */
function isPointMode(modeKey) {
    return modeKey === 'latlon' || modeKey === 'geom_adresse';
}

/**
 * Regroupe les features Point ayant les mêmes coordonnées en une feature "stack".
 * Le stack stocke le nombre d'éléments (_stackCount) et leurs IDs (_stackIds JSON).
 */
function mergeColocated(features, colorField) {
    const byCoord = new Map();
    features.forEach(f => {
        if (f.geometry?.type !== 'Point') return;
        const [lon, lat] = f.geometry.coordinates;
        const key = `${lon.toFixed(6)},${lat.toFixed(6)}`;
        if (!byCoord.has(key)) byCoord.set(key, []);
        byCoord.get(key).push(f);
    });
    const result = [];
    byCoord.forEach(group => {
        if (group.length === 1) {
            result.push(group[0]);
        } else {
            const colorVal = colorField ? group[0].properties[colorField] : undefined;
            result.push({
                type: 'Feature',
                geometry: group[0].geometry,
                properties: {
                    _stackCount: group.length,
                    _stackIds: JSON.stringify(
                        group.map(f => f.properties['meta.index'])
                    ),
                    ...(colorVal != null ? {[colorField]: colorVal} : {}),
                },
            });
        }
    });
    return result;
}

/**
 * Pour les modes polygone groupe (geom_groupe) : toutes les constructions d'un
 * même groupe partagent la même géométrie → regrouper par batiment_groupe_id.
 */
function mergeByGroupeId(features, colorField) {
    const byGroupe = new Map();
    features.forEach(f => {
        const gid = f.properties?.['meta.batiment_groupe_id'] ?? '__no_group__';
        if (!byGroupe.has(gid)) byGroupe.set(gid, []);
        byGroupe.get(gid).push(f);
    });
    const result = [];
    byGroupe.forEach(group => {
        if (group.length === 1) {
            result.push(group[0]);
        } else {
            const colorVal = colorField ? group[0].properties[colorField] : undefined;
            result.push({
                type: 'Feature',
                geometry: group[0].geometry,
                properties: {
                    _stackCount: group.length,
                    _stackIds: JSON.stringify(
                        group.map(f => f.properties['meta.index'])
                    ),
                    ...(colorVal != null ? {[colorField]: colorVal} : {}),
                },
            });
        }
    });
    return result;
}

/**
 * Pour geom_parcelle : un groupe peut couvrir plusieurs parcelles distinctes.
 * On déduplique par géométrie (même WKT/GeoJSON = même polygone) pour éviter
 * d'afficher plusieurs fois la même parcelle sans perdre les parcelles distinctes.
 */
function deduplicateByGeometry(features) {
    const seen = new Set();
    return features.filter(f => {
        const key = JSON.stringify(f.geometry);
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
    });
}

// ── Popup contenu simple ──────────────────────────────────────────────────────

function PopupContent({properties}) {
    const bid = properties['meta.batiment_construction_id'];
    const gid = properties['meta.batiment_groupe_id'];

    // Grouper les propriétés par préfixe source (hors identifiants déjà affichés)
    const groups = {};
    Object.entries(properties).forEach(([key, val]) => {
        if (['meta.batiment_construction_id', 'meta.batiment_groupe_id'].includes(key)) return;
        const [prefix] = key.split('.');
        if (!groups[prefix]) groups[prefix] = [];
        groups[prefix].push({key, val: String(val ?? '')});
    });

    return (
        <Box sx={{minWidth: 220, maxWidth: 320, maxHeight: 360, overflowY: 'auto', p: 0.5}}>
            {/* En-tête composite : IDs */}
            <Box sx={{mb: 0.6, pb: 0.5, borderBottom: '2px solid #e3e8f0'}}>
                {bid && <Box sx={{fontFamily: 'monospace', fontSize: '0.65rem', fontWeight: 700, color: '#1a1a1a', lineHeight: 1.5}}>{bid}</Box>}
                {gid && <Box sx={{fontFamily: 'monospace', fontSize: '0.6rem', color: '#555', lineHeight: 1.4}}>{gid}</Box>}
            </Box>

            {Object.entries(groups).map(([prefix, items]) => {
                const src = SOURCE_PREFIX_MAP[prefix];
                return (
                    <Box key={prefix} sx={{mb: 0.5}}>
                        <Chip
                            label={src?.label ?? prefix}
                            size="small"
                            sx={{
                                height: 16, fontSize: '0.6rem', fontWeight: 700,
                                bgcolor: (src?.color ?? '#666') + '22',
                                color: src?.color ?? '#666',
                                mb: 0.4,
                            }}
                        />
                        {items.slice(0, 8).map(({key, val}) => {
                            const col = key.split('.').slice(1).join('.');
                            return (
                                <Box key={key} sx={{
                                    display: 'flex', gap: 1,
                                    py: '1px',
                                    borderBottom: '1px solid #f0f0f0',
                                }}>
                                    <Typography variant="caption" sx={{
                                        color: 'text.secondary', fontFamily: 'monospace',
                                        fontSize: '0.68rem', minWidth: 90, flexShrink: 0,
                                    }}>
                                        {col}
                                    </Typography>
                                    <Typography variant="caption" sx={{
                                        fontWeight: 500, fontSize: '0.72rem',
                                        wordBreak: 'break-word',
                                    }}>
                                        {val.length > 60 ? val.slice(0, 60) + '…' : val}
                                    </Typography>
                                </Box>
                            );
                        })}
                        {items.length > 8 && (
                            <Typography variant="caption" sx={{color: 'text.disabled', fontSize: '0.65rem'}}>
                                +{items.length - 8} champs…
                            </Typography>
                        )}
                        <Divider sx={{mt: 0.5}}/>
                    </Box>
                );
            })}
        </Box>
    );
}

// ── Helpers adresse ───────────────────────────────────────────────────────────

/**
 * Reconstruit un libellé d'adresse lisible depuis les champs `adresses.*` d'un row.
 * Essaie plusieurs noms de champs connus du BDNB, puis compose depuis les parties,
 * et retombe sur `meta.cle_interop_adr` en dernier recours.
 */
function getAddressLabel(row) {
    if (!row) return '';

    // Libellé complet BAN (champ le plus lisible)
    const full = row['adresses.libelle_adr_principale_ban']
               || row['adresses.adresse_ban']
               || row['adresses.adresse_postale_brute'];
    if (full) return full;

    // Composition depuis les parties
    const fields = {};
    Object.entries(row).forEach(([k, v]) => {
        if (k.startsWith('adresses.')) fields[k.slice(9)] = v;
    });

    const num   = fields.rep_de_voie  || fields.rep       || fields.numero || '';
    const voie  = fields.nom_de_voie  || fields.libelle_voie || fields.voie || '';
    const cp    = fields.code_postal  || '';
    const ville = fields.libelle_commune || fields.nom_commune || fields.commune || '';

    const composed = [num, voie, cp, ville].filter(Boolean).join(' ');
    if (composed.trim()) return composed;

    // Dernier recours : clé interop (lisible, ex: "75056_0001_00042")
    return row['meta.cle_interop_adr'] || '';
}

// ── Popup stack (plusieurs constructions à la même position) ─────────────────

function StackPopupContent({ids, csvData}) {
    const [detail, setDetail] = React.useState(null);

    if (detail) {
        const row = csvData?.rows?.[detail];
        const properties = {};
        if (row) {
            Object.entries(row).forEach(([k, v]) => {
                properties[k] = Array.isArray(v) ? v.join(' | ') : v;
            });
        }
        return (
            <Box sx={{minWidth: 220, maxWidth: 320}}>
                <Box onClick={() => setDetail(null)} sx={{
                    cursor: 'pointer', display: 'inline-flex', alignItems: 'center',
                    gap: 0.4, mb: 0.5, color: '#1976d2',
                }}>
                    <Typography variant="caption" sx={{fontSize: '0.65rem', fontWeight: 600}}>
                        ← {ids.length} bâtiments
                    </Typography>
                </Box>
                <PopupContent properties={properties}/>
            </Box>
        );
    }

    return (
        <Box sx={{minWidth: 250, maxWidth: 320, p: 0.5}}>
            <Typography variant="caption" sx={{
                fontWeight: 700, fontSize: '0.7rem',
                color: '#607d8b', display: 'block', mb: 0.6,
            }}>
                {ids.length} bâtiments à cette position
            </Typography>
            <Box sx={{maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '4px'}}>
                {ids.map((id, idx) => {
                    const row = csvData?.rows?.[id];
                    if (!row) return null;
                    const dpe = row['meta.etiquette_dpe_median'];
                    const ges = row['meta.etiquette_ges_median'];
                    const su  = row['meta.surface_utile_calc'];
                    return (
                        <Box key={id} onClick={() => setDetail(id)} sx={{
                            cursor: 'pointer', p: '5px 8px', borderRadius: 1,
                            border: '1px solid #e0e0e0',
                            '&:hover': {bgcolor: '#f0f4ff', borderColor: '#90caf9'},
                        }}>
                            {/* Index de l'élément + adresse lisible en titre */}
                            <Box sx={{display: 'flex', alignItems: 'baseline', gap: 0.6, mb: '2px'}}>
                                <Box sx={{fontFamily: 'monospace', fontSize: '0.62rem', fontWeight: 700, color: '#607d8b', flexShrink: 0}}>
                                    #{idx + 1}
                                </Box>
                                {(() => {
                                    const addr = getAddressLabel(row);
                                    return addr ? (
                                        <Box sx={{fontSize: '0.7rem', fontWeight: 700, color: '#1a237e', lineHeight: 1.4}}>
                                            {addr}
                                        </Box>
                                    ) : null;
                                })()}
                            </Box>
                            <Box sx={{display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center'}}>
                                {dpe && (
                                    <Chip label={`DPE ${dpe}`} size="small" sx={{
                                        height: 16, fontSize: '0.6rem', fontWeight: 700,
                                        bgcolor: (CLASS_COLORS[dpe] ?? '#aaa') + '33',
                                        color: CLASS_COLORS[dpe] ?? '#666',
                                    }}/>
                                )}
                                {ges && (
                                    <Chip label={`GES ${ges}`} size="small" sx={{
                                        height: 16, fontSize: '0.6rem',
                                        bgcolor: '#eeeeee', color: '#555',
                                    }}/>
                                )}
                                {su > 0 && (
                                    <Typography variant="caption" sx={{fontSize: '0.62rem', color: '#666'}}>
                                        {Number(su).toLocaleString('fr-FR')} m²
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}

// ── Composant principal ───────────────────────────────────────────────────────

export default function MapView({csvData, columnVisibilityModel, filteredIds, geoMode = 'latlon', savedViewState, onViewStateChange}) {
    const mapRef = React.useRef();
    const [popup, setPopup] = React.useState(null);
    const [cursor, setCursor] = React.useState('auto');
    const [colorField, setColorField] = React.useState('meta.etiquette_dpe_median');

    const pointMode   = isPointMode(geoMode);
    const clusterMode = false; // stack maison pour tous les modes (geom_adresse inclus)
    const modeInfo    = GEOJSON_EXPORT_MODES.find(m => m.key === geoMode);

    // Fermer le popup quand le mode change
    React.useEffect(() => { setPopup(null); }, [geoMode]);

    // ── Options de coloration : DPE/GES + champs numériques visibles dans les sources ──
    const colorOptions = React.useMemo(() => {
        const isVisible = (key) => columnVisibilityModel[key.replace(/\./g, '__')] !== false;
        const opts = [];
        // DPE et GES : inclure seulement si la colonne est visible
        if (isVisible('meta.etiquette_dpe_median')) opts.push({key: 'meta.etiquette_dpe_median', label: 'DPE médian'});
        if (isVisible('meta.etiquette_dpe_moyen'))  opts.push({key: 'meta.etiquette_dpe_moyen',  label: 'DPE moyen'});
        if (isVisible('meta.etiquette_ges_median')) opts.push({key: 'meta.etiquette_ges_median', label: 'GES médian'});
        if (isVisible('meta.etiquette_ges_moyen'))  opts.push({key: 'meta.etiquette_ges_moyen',  label: 'GES moyen'});
        if (!csvData) return opts;

        // Champs à ne jamais proposer dans la coloration
        const EXCLUDE = new Set(['meta.index', 'meta.latitude', 'meta.longitude']);

        Object.entries(csvData.meta).forEach(([key, meta]) => {
            if (meta.type !== 'number') return;
            if (EXCLUDE.has(key)) return;
            if (!isVisible(key)) return;
            const parts = key.split('.');
            // label = "prefix › colonne" pour distinguer les sources
            const prefix = parts[0];
            const col    = parts.slice(1).join('.');
            const label  = prefix !== 'meta'
                ? `${SOURCE_PREFIX_MAP[prefix]?.label ?? prefix} › ${col}`
                : col;
            opts.push({key, label});
        });
        return opts;
    }, [csvData, columnVisibilityModel]);

    // Réinitialiser colorField si le champ courant n'est plus dans les options
    React.useEffect(() => {
        if (colorOptions.length > 0 && !colorOptions.some(o => o.key === colorField)) {
            setColorField(colorOptions[0].key);
        }
    }, [colorOptions, colorField]);
    const {minVal, maxVal} = React.useMemo(() => {
        if (CLASS_FIELDS.has(colorField) || !csvData) return {minVal: 0, maxVal: 1};
        let min = Infinity, max = -Infinity;
        Object.values(csvData.rows).forEach(row => {
            const v = Number(row[colorField]);
            if (isFinite(v)) { if (v < min) min = v; if (v > max) max = v; }
        });
        return {minVal: min === Infinity ? 0 : min, maxVal: max === -Infinity ? 1 : max};
    }, [colorField, csvData]);

    // ── Expression de couleur selon le champ sélectionné ─────────────────────
    const colorExpr = React.useMemo(() => {
        if (CLASS_FIELDS.has(colorField)) return buildClassExpr(colorField);
        return buildRampExpr(colorField, minVal, maxVal);
    }, [colorField, minVal, maxVal]);

    // ── Layers calculés depuis l'expression courante ──────────────────────────
    const unclusteredLayer    = React.useMemo(() => makeUnclusteredLayer(colorExpr),    [colorExpr]);
    const stackLayer          = React.useMemo(() => makeStackLayer(colorExpr),          [colorExpr]);
    const polygonFillLayer    = React.useMemo(() => makePolygonFillLayer(colorExpr),    [colorExpr]);
    const polygonOutlineLayer = React.useMemo(() => makePolygonOutlineLayer(colorExpr), [colorExpr]);

    // ── GeoJSON depuis les données filtrées + mode sélectionné ───────────────
    const geojson = React.useMemo(() => {
        if (!csvData) return {type: 'FeatureCollection', features: []};
        let features = buildGeoJsonFeatures(csvData, columnVisibilityModel, geoMode, filteredIds);
        // Regrouper les features co-localisées selon le mode
        if (geoMode === 'latlon' || geoMode === 'geom_adresse') {
            features = mergeColocated(features, colorField);
        } else if (geoMode === 'geom_groupe') {
            // Un polygone par batiment_groupe (fusionne les constructions du même groupe)
            features = mergeByGroupeId(features, colorField);
        } else if (geoMode === 'geom_parcelle') {
            // Un polygone par géométrie unique (un groupe peut couvrir plusieurs parcelles)
            features = deduplicateByGeometry(features);
        }
        // geom_cstr : déduplication WKT gérée dans geoJsonUtils (même construction)
        return {type: 'FeatureCollection', features};
    }, [csvData, columnVisibilityModel, geoMode, filteredIds, colorField]);

    // ── Centrage initial : viewport sauvegardé ou calcul sur les données ─────
    const initialViewState = React.useMemo(() => {
        if (savedViewState) return savedViewState;
        const features = geojson.features;
        if (!features.length) return {longitude: 2.3522, latitude: 46.8566, zoom: 5};
        const coords = features.flatMap(f => {
            const g = f.geometry;
            if (!g) return [];
            if (g.type === 'Point') return [g.coordinates];
            if (g.type === 'MultiPolygon') return g.coordinates.flat(2);
            if (g.type === 'Polygon') return g.coordinates.flat(1);
            return [];
        });
        if (!coords.length) return {longitude: 2.3522, latitude: 46.8566, zoom: 5};
        const lons = coords.map(c => c[0]);
        const lats = coords.map(c => c[1]);
        return {
            longitude: (Math.min(...lons) + Math.max(...lons)) / 2,
            latitude:  (Math.min(...lats) + Math.max(...lats)) / 2,
            zoom: 12,
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Layers interactifs selon le mode ──────────────────────────────────────
    const interactiveLayers = pointMode
        ? [...(clusterMode ? ['clusters'] : ['stacked-point']), 'unclustered-point']
        : ['polygon-fill'];

    // ── Click handler ─────────────────────────────────────────────────────────
    const handleClick = React.useCallback(async (event) => {
        const feature = event.features?.[0];
        if (!feature) { setPopup(null); return; }

        if (feature.layer.id === 'clusters') {
            const zoom = await mapRef.current
                .getSource('batiments')
                .getClusterExpansionZoom(feature.properties.cluster_id);
            mapRef.current.flyTo({center: feature.geometry.coordinates, zoom: zoom + 1, duration: 600});
        } else if (feature.properties._stackCount) {
            // Points co-localisés → popup liste
            const ids = JSON.parse(feature.properties._stackIds || '[]');
            setPopup({longitude: event.lngLat.lng, latitude: event.lngLat.lat, type: 'stack', ids});
        } else {
            // Point ou polygone simple
            setPopup({longitude: event.lngLat.lng, latitude: event.lngLat.lat, type: 'single', properties: feature.properties});
        }
    }, []);

    if (!MAPBOX_TOKEN || MAPBOX_TOKEN === 'your_mapbox_token_here') {
        return (
            <Box sx={{mt: 2, mx: 2}}>
                <Alert severity="warning">
                    Aucun token Mapbox configuré. Ajoutez <code>REACT_APP_MAPBOX_TOKEN=pk.xxx</code> dans <code>.env</code>.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{mt: 2, mx: 2, height: 'calc(100vh - 130px)', borderRadius: 2, overflow: 'hidden', boxShadow: 1}}>
            <MapboxMap
                ref={mapRef}
                mapboxAccessToken={MAPBOX_TOKEN}
                initialViewState={initialViewState}
                style={{width: '100%', height: '100%'}}
                mapStyle="mapbox://styles/mapbox/streets-v9"
                interactiveLayerIds={interactiveLayers}
                cursor={cursor}
                onClick={handleClick}
                onMouseEnter={() => setCursor('pointer')}
                onMouseLeave={() => setCursor('auto')}
                onMove={e => onViewStateChange?.(e.viewState)}
            >
                <NavigationControl position="top-right"/>
                <ScaleControl/>
                <FullscreenControl position="top-right"/>

                {/* Badge du mode actif */}
                {modeInfo && (
                    <Box sx={{
                        position: 'absolute', top: 10, left: 10,
                        bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 2,
                        px: 1.5, py: 0.5, boxShadow: 1, pointerEvents: 'none',
                    }}>
                        <Typography variant="caption" sx={{fontWeight: 700, fontSize: '0.72rem', color: '#1976d2'}}>
                            {modeInfo.label}
                        </Typography>
                        <Typography variant="caption" sx={{display: 'block', fontSize: '0.65rem', color: 'text.secondary'}}>
                            {geojson.features.length.toLocaleString('fr-FR')} élément{geojson.features.length > 1 ? 's' : ''}
                        </Typography>
                    </Box>
                )}

                {/* Sélecteur de dimension de couleur */}
                <Box sx={{
                    position: 'absolute', top: 68, left: 10,
                    bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 2,
                    px: 1.2, py: 0.6, boxShadow: 1, minWidth: 150,
                }}>
                    <Typography variant="caption" sx={{fontSize: '0.6rem', color: '#888', display: 'block', mb: 0.3}}>
                        Coloration
                    </Typography>
                    <Select
                        value={colorOptions.some(o => o.key === colorField) ? colorField : colorOptions[0]?.key ?? ''}
                        onChange={e => setColorField(e.target.value)}
                        variant="standard"
                        disableUnderline
                        sx={{fontSize: '0.72rem', fontWeight: 600, width: '100%'}}
                    >
                        {colorOptions.map(({key, label}) => (
                            <MenuItem key={key} value={key} sx={{fontSize: '0.72rem'}}>{label}</MenuItem>
                        ))}
                    </Select>
                </Box>

                {/* Légende */}
                <Box sx={{
                    position: 'absolute', bottom: 30, left: 10,
                    bgcolor: 'rgba(255,255,255,0.92)', borderRadius: 2,
                    px: 1.2, py: 0.8, boxShadow: 1, pointerEvents: 'none', minWidth: 110,
                }}>
                    {CLASS_FIELDS.has(colorField) ? (
                        /* Légende classes A-G */
                        <>
                            {Object.entries(CLASS_COLORS).map(([letter, color]) => (
                                <Box key={letter} sx={{display: 'flex', alignItems: 'center', gap: 0.6, mb: '2px'}}>
                                    <Box sx={{width: 12, height: 12, borderRadius: '2px', bgcolor: color, flexShrink: 0}}/>
                                    <Typography variant="caption" sx={{fontSize: '0.65rem', fontWeight: 600, color: '#333'}}>
                                        {letter}
                                    </Typography>
                                </Box>
                            ))}
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.6, mt: '2px'}}>
                                <Box sx={{width: 12, height: 12, borderRadius: '2px', bgcolor: '#aaaaaa', flexShrink: 0}}/>
                                <Typography variant="caption" sx={{fontSize: '0.65rem', color: '#888'}}>N/A</Typography>
                            </Box>
                        </>
                    ) : (
                        /* Légende ramp min→max */
                        <>
                            <Box sx={{
                                width: '100%', height: 10, borderRadius: 1, mb: 0.4,
                                background: `linear-gradient(to right, ${RAMP_MIN_COLOR}, ${RAMP_MAX_COLOR})`,
                            }}/>
                            <Box sx={{display: 'flex', justifyContent: 'space-between'}}>
                                <Typography variant="caption" sx={{fontSize: '0.62rem', color: '#555'}}>
                                    {minVal % 1 === 0 ? minVal : minVal.toFixed(1)}
                                </Typography>
                                <Typography variant="caption" sx={{fontSize: '0.62rem', color: '#555'}}>
                                    {maxVal % 1 === 0 ? maxVal : maxVal.toFixed(1)}
                                </Typography>
                            </Box>
                            <Box sx={{display: 'flex', alignItems: 'center', gap: 0.6, mt: 0.4}}>
                                <Box sx={{width: 12, height: 12, borderRadius: '2px', bgcolor: '#dddddd', flexShrink: 0}}/>
                                <Typography variant="caption" sx={{fontSize: '0.62rem', color: '#888'}}>N/A</Typography>
                            </Box>
                        </>
                    )}
                </Box>

                <Source
                    id="batiments"
                    type="geojson"
                    data={geojson}
                    cluster={clusterMode}
                    clusterMaxZoom={15}
                    clusterRadius={50}
                >
                    {/* Layers Points – cluster Mapbox (désactivé) / stacks et points individuels */}
                    {clusterMode && <Layer {...clusterLayer}/>}
                    {clusterMode && <Layer {...clusterCountLayer}/>}
                    {/* Points co-localisés */}
                    {!clusterMode && pointMode && <Layer {...stackLayer}/>}
                    {!clusterMode && pointMode && <Layer {...stackCountLayer}/>}
                    {/* Points individuels */}
                    {pointMode && <Layer {...unclusteredLayer}/>}

                    {/* Layers Polygones */}
                    {!pointMode && <Layer {...polygonFillLayer}/>}
                    {!pointMode && <Layer {...polygonOutlineLayer}/>}
                    {/* Badge stack centré sur les polygones groupés */}
                    {!pointMode && <Layer {...stackCountPolygonLayer}/>}
                </Source>

                {popup && (
                    <Popup
                        longitude={popup.longitude}
                        latitude={popup.latitude}
                        onClose={() => setPopup(null)}
                        closeOnClick={false}
                        maxWidth="340px"
                    >
                        {popup.type === 'stack'
                            ? <StackPopupContent ids={popup.ids} csvData={csvData}/>
                            : <PopupContent properties={popup.properties ?? {}}/>
                        }
                    </Popup>
                )}
            </MapboxMap>
        </Box>
    );
}

